// src/features/frighi/api.js
// Wrapper API Cryo robusto + retro-compatibile

// --- Helpers
const toParams = (obj) => {
  const p = new URLSearchParams();
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    p.set(k, String(v));
  });
  return p.toString();
};

// ---------- LISTA ----------
/**
 * getCryoList
 * Chiamate valide:
 *   getCryoList();                       // default
 *   getCryoList({ q: 'mac', limit: 50 }); 
 *   getCryoList({ aziendaId: 12 });
 *   getCryoList(12);                     // retro-compat (aziendaId)
 *   getCryoList(null);                   // non esplode
 */
export async function getCryoList(opts) {
  // retro-compat: se passano un numero/stringa, trattalo come aziendaId
  if (typeof opts === 'number' || typeof opts === 'string') {
    opts = { aziendaId: opts };
  }
  // fallback sicuro
  const { q = '', order = 'last_seen_desc', limit = 200, aziendaId = null } = (opts || {});

  const qs = toParams({ q, order, limit, azienda_id: aziendaId });
  const url = qs ? `/api/api_cryo_list.php?${qs}` : `/api/api_cryo_list.php`;

  const res = await fetch(url, { credentials: 'include' });
  const data = await res.json();
  if (!data || !data.ok) throw new Error((data && data.error) || 'Errore nel caricamento lista Cryo');
  return data.items || []; // l’API ritorna { ok, count, items }
}

// ---------- STORICO ----------
/**
 * getCryoHistory({ deviceId, mac, days=1, limit=2000 })
 * NB: supporta anche old keys { id } -> deviceId (retro-compat)
 */
export async function getCryoHistory(opts) {
  const o = opts || {};
  const deviceId = o.deviceId ?? o.id ?? null; // retro-compat
  const mac = o.mac ?? null;
  const days = Number.isFinite(o.days) ? o.days : 1;
  const limit = Number.isFinite(o.limit) ? o.limit : 2000;

  if (!deviceId && !mac) throw new Error('Specificare deviceId o mac');

  let from = null;
  if (days && days > 0) {
    const d = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    from = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  const qs = toParams({ device_id: deviceId, mac, from, limit });
  const res = await fetch(`/api/api_cryo_history.php?${qs}`, { credentials: 'include' });
  const data = await res.json();
  if (!data || !data.ok) throw new Error((data && data.error) || 'Errore nel caricamento storico');
  return data; // { ok, device_id, count, items: [...] }
}

// ---------- AGGIORNA LIMITI ----------
/**
 * setCryoLimits({ deviceId, nome, azienda_id, limite_min, limite_max })
 * Retro-compat: accetta anche { id } -> deviceId, ignora 'mac'
 */
export async function setCryoLimits(opts) {
  const o = opts || {};

  const frigoId =
    o.frigoId ??
    o.frigo_id ??
    null;

  const deviceId =
    o.deviceId ??
    o.device_id ??
    o.id ??
    null;

  const macAddress =
    o.mac ??
    o.mac_address ??
    null;

  const hasFrigo =
    frigoId !== null &&
    frigoId !== undefined &&
    String(frigoId) !== "";

  const hasDevice =
    deviceId !== null &&
    deviceId !== undefined &&
    String(deviceId) !== "" &&
    Number(deviceId) > 0;

  const hasMac =
    macAddress !== null &&
    macAddress !== undefined &&
    String(macAddress).trim() !== "";

  if (!hasFrigo && !hasDevice && !hasMac) {
    throw new Error("frigo_id, device_id o mac_address mancanti");
  }

  const form = new FormData();

  if (hasFrigo) form.append("frigo_id", String(frigoId));
  if (hasDevice) form.append("device_id", String(deviceId));
  if (hasMac) form.append("mac_address", String(macAddress).trim());

  if (o.nome !== undefined) form.append("nome", String(o.nome ?? ""));
  if (o.azienda_id !== undefined) {
    form.append("azienda_id", o.azienda_id === null ? "" : String(o.azienda_id));
  }
  if (o.tipo_conservazione !== undefined && o.tipo_conservazione !== null) {
    form.append("tipo_conservazione", String(o.tipo_conservazione));
  }
  if (o.tipo !== undefined && o.tipo !== null) {
    form.append("tipo", String(o.tipo));
  }
  if (o.limite_min !== undefined && o.limite_min !== null) {
    form.append("limite_min", String(o.limite_min));
  }
  if (o.limite_max !== undefined && o.limite_max !== null) {
    form.append("limite_max", String(o.limite_max));
  }

  const res = await fetch("/api/api_cryo_set_limits.php", {
    method: "POST",
    credentials: "include",
    body: form,
  });

  const data = await res.json();
  if (!data || !data.ok) {
    throw new Error((data && (data.message || data.error)) || "Errore aggiornamento limiti");
  }

  return true;
}
// ---------- INSERIMENTO MANUALE TEMPERATURA ----------
/**
 * postCryoManualTemp({ frigo_id, dispositivo_id, mac_address, temperatura })
 * Salva una lettura manuale con timestamp server.
 */
export async function postCryoManualTemp(opts) {
  const o = opts || {};

  const frigoId =
    o.frigoId ??
    o.frigo_id ??
    null;

  const dispositivoId =
    o.dispositivoId ??
    o.dispositivo_id ??
    o.deviceId ??
    null;

  const mac =
    o.mac ??
    o.mac_address ??
    null;

  const temperatura = o.temperatura;

  const hasFrigo =
    frigoId !== null &&
    frigoId !== undefined &&
    String(frigoId) !== "";

  const hasDispositivo =
    dispositivoId !== null &&
    dispositivoId !== undefined &&
    String(dispositivoId) !== "" &&
    Number(dispositivoId) > 0;

  const hasMac =
    mac !== null &&
    mac !== undefined &&
    String(mac).trim() !== "";

  if (!hasFrigo && !hasDispositivo && !hasMac) {
    throw new Error("Specificare frigo_id, dispositivo_id o mac_address");
  }

  if (temperatura === undefined || temperatura === null || temperatura === "") {
    throw new Error("Temperatura mancante");
  }

  const form = new FormData();
  if (hasFrigo) form.append("frigo_id", String(frigoId));
  if (hasDispositivo) form.append("dispositivo_id", String(dispositivoId));
  if (hasMac) form.append("mac_address", String(mac).trim());
  form.append("temperatura", String(temperatura));

  const res = await fetch("/api/api_cryo_manual_temp.php", {
    method: "POST",
    credentials: "include",
    body: form,
  });

  const data = await res.json();
  if (!data || !data.ok) {
    throw new Error((data && (data.hint || data.error)) || "Errore salvataggio lettura manuale");
  }

  return data;
}
